import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExternalLink, Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetch(`${API}/policies`)
      .then(res => res.json())
      .then(data => setPolicies(data))
      .catch(err => console.error(err));
  }, []);

  // Get unique categories
  const categories = ["all", ...new Set(policies.map(p => p.category).filter(Boolean))];

  // Filter policies
  const filteredPolicies = selectedCategory === "all" 
    ? policies 
    : policies.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-transparent to-primary/10 noise-texture">
      {/* Admin Link in Corner */}
      <div className="absolute top-4 right-4 z-50">
        <Button
          data-testid="admin-login-btn"
          onClick={() => navigate('/admin')}
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-primary"
        >
          <Lock className="w-4 h-4 mr-1" />
          Admin
        </Button>
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1592495989226-03f88104f8cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxmaW5hbmNpYWwlMjBwb2xpY3klMjBkYXRhJTIwY2hhcnRzJTIwZ3JhcGhzJTIwYnVzaW5lc3MlMjBhbmFseXNpc3xlbnwwfHx8fDE3NjYyNjIxNzF8MA&ixlib=rb-4.1.0&q=85')] bg-cover bg-center opacity-5"></div>
        
        <div className="relative container mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground">
              PolicyPulse
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
              Understanding how different age groups make financial and investment decisions in response to public policy changes
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                data-testid="start-survey-btn"
                onClick={() => navigate('/survey')} 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-8 rounded-full font-medium transition-transform active:scale-95"
              >
                Start Survey
              </Button>
            </div>
            {policies.length > 0 && (
              <p className="text-sm text-muted-foreground" data-testid="policy-count">
                {policies.length} active verified {policies.length === 1 ? 'policy' : 'policies'} available for research
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              We use browser data to prevent duplicate submissions
            </p>
          </div>
        </div>
      </div>

      {/* Why These Policies Section */}
      <div className="py-16 bg-white border-y border-border">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6 text-center">Why These Policies?</h2>
            <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
              <p className="leading-relaxed">
                The policies in this survey were carefully selected to represent a <strong className="text-foreground">diverse cross-section of recent U.S. public policy changes</strong> that directly impact financial and investment decisions across different demographics.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                <div className="bg-primary/5 p-5 rounded-xl border border-primary/20">
                  <h3 className="font-semibold text-foreground mb-2">Recency & Relevance</h3>
                  <p className="text-sm">All policies are from 2024-2025, ensuring responses reflect current economic sentiment and real-world impact on financial planning.</p>
                </div>
                <div className="bg-primary/5 p-5 rounded-xl border border-primary/20">
                  <h3 className="font-semibold text-foreground mb-2">Broad Economic Impact</h3>
                  <p className="text-sm">From Federal Reserve rate decisions to tax reforms, these policies affect savings, investments, retirement planning, and day-to-day finances.</p>
                </div>
                <div className="bg-primary/5 p-5 rounded-xl border border-primary/20">
                  <h3 className="font-semibold text-foreground mb-2">Cross-Generational Effects</h3>
                  <p className="text-sm">Policies like student loan reform impact younger demographics, while Social Security and retirement policies affect older generations differently.</p>
                </div>
                <div className="bg-primary/5 p-5 rounded-xl border border-primary/20">
                  <h3 className="font-semibold text-foreground mb-2">Verified Sources</h3>
                  <p className="text-sm">Each policy is sourced from official government documents, Federal Reserve announcements, and reputable economic research institutions.</p>
                </div>
              </div>
              <p className="leading-relaxed">
                By studying emotional and behavioral responses to these specific policies, we aim to understand <strong className="text-foreground">how different age groups perceive financial risk and opportunity</strong> in response to government actions—insights valuable for policymakers, financial advisors, and academic researchers.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Policy Visualization */}
      <div className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">Research-Driven Insights</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Our platform combines behavioral economics with advanced data analytics to understand how policy changes influence investment decisions across generations.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white rounded-lg border border-border shadow-sm">
                  <p className="text-3xl font-bold text-primary font-mono">{policies.length}</p>
                  <p className="text-sm text-muted-foreground">Active Policies</p>
                </div>
                <div className="p-4 bg-white rounded-lg border border-border shadow-sm">
                  <p className="text-3xl font-bold text-primary font-mono">6</p>
                  <p className="text-sm text-muted-foreground">Age Groups</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1618044733300-9472054094ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBwb2xpY3klMjBkYXRhJTIwY2hhcnRzJTIwZ3JhcGhzJTIwYnVzaW5lc3MlMjBhbmFseXNpc3xlbnwwfHx8fDE3NjYyNjIxNzF8MA&ixlib=rb-4.1.0&q=85" 
                alt="Financial policy data and charts" 
                className="w-full h-80 object-cover rounded-xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* All Policies Section */}
      <div className="py-24">
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">Current Policy Research Topics</h2>
            <p className="text-lg text-muted-foreground">All policies verified with official government sources • Click to view original legislation</p>
          </div>

          {/* Category Tabs */}
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-6">
            <TabsList className="flex-wrap h-auto gap-2 bg-secondary/50 p-2 justify-center">
              {categories.map(category => (
                <TabsTrigger 
                  key={category} 
                  value={category}
                  data-testid={`home-category-tab-${category}`}
                  className="capitalize"
                >
                  {category === "all" ? "All Categories" : category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Policies Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPolicies.map((policy) => (
              <Card 
                key={policy.id} 
                data-testid={`home-policy-card-${policy.id}`}
                className="border border-border/50 shadow-sm hover:shadow-lg transition-all rounded-xl hover-lift"
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs font-medium px-2 py-1 bg-primary/10 text-primary rounded-md">
                      {policy.category || "General"}
                    </span>
                    {policy.url && (
                      <a 
                        href={policy.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/70 transition-colors"
                        data-testid={`policy-link-${policy.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                  <CardTitle className="text-lg mb-2">
                    {policy.url ? (
                      <a 
                        href={policy.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:text-primary transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {policy.title}
                      </a>
                    ) : (
                      policy.title
                    )}
                  </CardTitle>
                  {policy.date_effective && (
                    <p className="text-xs text-muted-foreground mb-2">
                      Effective: {policy.date_effective}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                    {policy.description.length > 150 
                      ? `${policy.description.substring(0, 150)}...` 
                      : policy.description}
                  </p>
                  {policy.source && (
                    <p className="text-xs text-muted-foreground italic">
                      Source: {policy.source}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section with Stock Market Image */}
      <div className="py-24">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">Ready to Contribute?</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Your responses help researchers understand the complex relationship between public policy, age demographics, and financial decision-making.
              </p>
              <Button 
                data-testid="cta-survey-btn"
                onClick={() => navigate('/survey')} 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-8 rounded-full font-medium transition-transform active:scale-95"
              >
                Take the Survey
              </Button>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1754965929667-41ed49e4a63a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxlY29ub21pYyUyMHBvbGljeSUyMGdvdmVybm1lbnQlMjBmaW5hbmNlJTIwc3RvY2slMjBtYXJrZXR8ZW58MHx8fHwxNzY2MjYyMTczfDA&ixlib=rb-4.1.0&q=85" 
                alt="Stock exchange and financial markets" 
                className="w-full h-80 object-cover rounded-xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;